import { type LiveChannelFormatted } from '$/channels'
import { type TvChannelWithLiveEpg } from '@/types'
import {
  CARDS_ASPECT_RATIO,
  CardsBreakpoints,
  FONTS,
  LiveChannelCard,
  RowContainerTemplate,
  RowControlsType,
  type IRowContainerProps,
} from '@setplex/arcane'
import { MediaTypes } from '@setplex/tria-api'
import { useUnit } from 'effector-react'
import { JSX, useCallback, type SyntheticEvent } from 'react'
import { epgCategoriesModel } from '~/entities/epg/categories'
import { model as session } from '~/entities/session'
import { model as favoritesModel } from '~/features/favorites'
import { addGoogleAnalyticsPrefixWithAuth } from '~/shared/helpers'
import * as epgAnalyticsWidgetModel from '~/widgets/epg/analytics'
import { LiveChannelRowCardsCount } from '../../constants'
interface IRowProps {
  id?: number | string
  content?: LiveChannelFormatted[] | TvChannelWithLiveEpg[]
  title?: IRowContainerProps['title']
  cardsCount?: LiveChannelRowCardsCount
  isSeeAllBtnShown?: boolean
  seeAllBtnClick?: ({ id, title }?: { id: number; title: string }) => void
  className?: string
  handleClick?: (data: string) => void
  onClickChannelCard?: () => void
  gradientOverlay?: boolean
  resetEpgBoard?: () => void
  onLoadMoreItems?: () => void
  activeChannelId?: number | string
  selectedChannelOrder?: number
  sendEpgChannelEventData?: boolean
  setTimeshift?: (
    timeshift: { enabled: boolean; shift: number; id: number } | null
  ) => void
}

ChannelRow.displayName = 'ChannelRow'
export function ChannelRow({
  id: rowId,
  content,
  title = '',
  className = '',
  cardsCount = LiveChannelRowCardsCount.DEFAULT,
  isSeeAllBtnShown,
  seeAllBtnClick,
  handleClick,
  onClickChannelCard,
  gradientOverlay = false,
  resetEpgBoard,
  onLoadMoreItems,
  activeChannelId,
  selectedChannelOrder,
  sendEpgChannelEventData,
  setTimeshift,
}: IRowProps): JSX.Element | null {
  const contentLength = content?.length ?? 0
  const isMoreThanOnePage = isSeeAllBtnShown ?? contentLength > cardsCount
  const favorite = useUnit(favoritesModel.$favorites)
  const handleFavoriteBtnClick = useUnit(favoritesModel.handleFavoriteBtnClick)
  const sliced =
    (onLoadMoreItems ? content : content?.slice(0, cardsCount)) ?? []
  const slicedLength = sliced.length
  const isAuthenticated = useUnit(session.$isAuthenticated)

  // regionId of the most recently loaded categories and channels
  const regionId = useUnit(epgCategoriesModel.$regionId)

  const onClick = () => {
    handleClick?.(title)
  }

  const handleSeeAllBtnClick = useCallback(() => {
    if (!seeAllBtnClick) return

    if (typeof rowId === 'number') {
      return seeAllBtnClick({ id: rowId, title })
    }
    return seeAllBtnClick() // Case if navigate to favorites for example
  }, [rowId, title, seeAllBtnClick])

  return slicedLength ? (
    <RowContainerTemplate
      className={`channel-row ${className}`}
      title={title}
      itemCount={slicedLength}
      breakpoints={CardsBreakpoints.ChannelCard}
      cardAspectRatio={CARDS_ASPECT_RATIO.HORIZONTAL_DEFAULT}
      controlsType={RowControlsType.inRowFluid}
      isMoreThanOnePage={isMoreThanOnePage}
      onSeeAllClick={handleSeeAllBtnClick}
      handleClick={onClick}
      titleFont={FONTS.SubtitleXlBold}
      onLoadMoreItems={onLoadMoreItems}
      selectedChannelOrder={selectedChannelOrder}
    >
      {sliced.map((card, index) => {
        const {
          id,
          logo,
          link,
          title: channelTitle,
          number: channelNumber,
          productionCountries,
          channelOwnerId,
          timeshift,
        } = card

        const firstProductionCountryId = productionCountries?.[0]?.id

        const isFavorite = Boolean(favorite.live_channel[id])
        const onClick = (e: SyntheticEvent) => {
          e.preventDefault()
          e.stopPropagation()

          handleFavoriteBtnClick?.({
            id,
            isFavorite: !isFavorite,
            contentType: MediaTypes.LIVE_CHANNEL,
            publisherId: channelOwnerId,
            contentTitle: channelTitle,
          })
        }
        const rowIdGA = rowId ? `row-${rowId}` : 'row'
        const idForGA = addGoogleAnalyticsPrefixWithAuth(
          `add-fav-channel-${rowIdGA}-${id}`,
          isAuthenticated
        )

        const handleClickChannelCard = () => {
          // reset EPG board if the channel is from a different region
          if (firstProductionCountryId !== regionId) {
            resetEpgBoard?.()
          }

          if (setTimeshift) {
            setTimeshift({ ...timeshift, id })
          }

          onClickChannelCard?.()
          if (sendEpgChannelEventData) {
            epgAnalyticsWidgetModel.sendEpgChannelEventData({
              channelId: String(id),
              channelTitle: channelTitle || '',
              channelOrderPosition: String(index + 1),
            })
          }
        }

        return (
          <LiveChannelCard
            key={`${index}${id}`}
            href={link}
            playLinkId={addGoogleAnalyticsPrefixWithAuth(
              `channel-play-${rowIdGA}-${id}`,
              isAuthenticated
            )}
            idForGA={idForGA}
            posterUrl={logo}
            isFavorite={isFavorite}
            handleFavoriteClick={onClick}
            onClick={handleClickChannelCard}
            alt={`"${channelTitle}" channel logo`}
            channelNumber={String(channelNumber)}
            isActive={activeChannelId === id}
            isTimeshiftEnabled={timeshift?.enabled}
            gradientOverlay={gradientOverlay}
          />
        )
      })}
    </RowContainerTemplate>
  ) : null
}
